/* Pickup & Delivery Scheduler
   Custom element that lets the customer choose a fulfillment method
   (Store Pickup / Delivery); pickup also takes a date and time slot, then
   persists the choice as cart (order) attributes so it shows on the order.

   Auto-initialises whenever inserted into the DOM (handles cart-drawer
   re-renders) because it extends HTMLElement and runs in connectedCallback. */

if (!customElements.get('pickup-delivery-scheduler')) {
  customElements.define(
    'pickup-delivery-scheduler',
    class PickupDeliveryScheduler extends HTMLElement {
      constructor() {
        super();
        this.DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']; // Monday-first columns
        this.DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        this.MONTHS = [
          'January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December',
        ];
        this.STORAGE_KEY = 'pds-selection';
      }

      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        try {
          this.config = JSON.parse(this.querySelector('.pds__config').textContent);
        } catch (e) {
          console.error('[pds] invalid config', e);
          return;
        }

        // DOM references
        this.methodBtns = Array.from(this.querySelectorAll('.pds__method'));
        this.locationEls = Array.from(this.querySelectorAll('[data-for-method]'));
        this.dateField = this.querySelector('.pds__field');
        this.trigger = this.querySelector('.pds__date-trigger');
        this.triggerText = this.querySelector('.pds__date-text');
        this.panel = this.querySelector('.pds__panel');
        this.calTitle = this.querySelector('.pds__cal-title');
        this.calGrid = this.querySelector('.pds__cal-grid');
        this.prevBtn = this.querySelector('.pds__cal-prev');
        this.nextBtn = this.querySelector('.pds__cal-next');
        this.slotsWrap = this.querySelector('.pds__slots');
        this.slotsList = this.querySelector('.pds__slots-list');
        this.errorEl = this.querySelector('.pds__error');
        this.loaderEl = this.querySelector('.pds__loader');

        // Boundaries
        this.minDate = this.startOfDay(new Date());
        this.minDate.setDate(this.minDate.getDate() + (this.config.leadDays || 0));
        this.maxDate = this.startOfDay(new Date());
        this.maxDate.setDate(this.maxDate.getDate() + (this.config.maxDaysAhead || 60));

        // State (restore from localStorage, fall back to saved cart attributes)
        this.state = this.restoreState();
        this.viewDate = this.state.date ? new Date(this.state.date) : new Date(this.minDate);
        this.viewDate.setDate(1);

        this.bindEvents();
        this.renderMethod();
        this.renderCalendar();
        this.renderTriggerSummary();
        this.enforceCheckout();

        // On load: strip any stale scheduling props left on line items (selection
        // is empty now), and record the current line items so we can detect when a
        // new one is added later.
        this.initCart();
        this.subscribeCartUpdates();
      }

      isComplete() {
        // Delivery is fulfilled by a third party — it only needs the method
        // chosen, no date/time. Pickup still requires a date and a time slot.
        if (this.state.method === 'delivery') return true;
        return !!(this.state.method && this.state.date && this.state.time);
      }

      /* ---------- cart sync & change detection ---------- */
      fetchCart() {
        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        return fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      }

      async initCart() {
        try {
          const cart = await this.fetchCart();
          // cartSignatures() ignores our own keys, so this is unaffected by
          // whether we restore or strip below.
          this._knownSigs = this.cartSignatures(cart.items || []);

          if (this.restoreFromCart(cart.items || [])) {
            // A still-valid selection is already saved on the cart lines (e.g.
            // the customer came back from checkout). Reflect it so checkout is
            // not needlessly blocked, and leave the saved props untouched.
            this.renderMethod();
            this.renderCalendar();
            this.renderSlots();
            this.renderTriggerSummary();
            this.enforceCheckout();
          } else {
            // No saved selection, or it is stale/invalid (e.g. a date that is now
            // in the past) → strip any leftover scheduling props so the customer
            // starts clean and must re-pick.
            await this._writeLineProps();
          }
        } catch (e) {
          console.error('[pds] init cart failed', e);
        }
      }

      // Reconstruct the selection from the scheduling props already on the cart
      // lines. Returns true (and updates this.state) only when the saved choice
      // is still bookable; otherwise returns false so the caller strips it.
      restoreFromCart(items) {
        const item = items.find((it) => it.properties && it.properties['Fulfillment Method']);
        if (!item) return false;

        const method = this.methodFromValue(item.properties['Fulfillment Method']);
        if (!method) return false;
        // The method must still be offered in the theme settings.
        if (method === 'pickup' && !this.config.pickupEnabled) return false;
        if (method === 'delivery' && !this.config.deliveryEnabled) return false;

        if (method === 'delivery') {
          // Delivery needs nothing more than the method.
          this.state.method = 'delivery';
          this.state.date = null;
          this.state.time = '';
          return true;
        }

        // Pickup must still resolve to a selectable date and an existing slot.
        const date = this.parseLongDate(item.properties.Date);
        const time = item.properties.Time || '';
        if (!date || date < this.minDate || date > this.maxDate || this.isClosed(date)) return false;
        if (this.slotsForDate(date).indexOf(time) === -1) return false;

        this.state.method = 'pickup';
        this.state.date = date;
        this.state.time = time;
        this.viewDate = new Date(date);
        this.viewDate.setDate(1);
        return true;
      }

      methodFromValue(value) {
        const labels = PickupDeliveryScheduler.METHOD_LABELS;
        if (value === labels.pickup) return 'pickup';
        if (value === labels.delivery) return 'delivery';
        return null;
      }

      // Parse a date string produced by formatDateLong (e.g. "Monday, 15 June
      // 2026"). Uses this.MONTHS so it stays in sync if the names ever change.
      parseLongDate(str) {
        if (!str) return null;
        const m = str.split(',').pop().trim().match(/^(\d{1,2})\s+(\S+)\s+(\d{4})$/);
        if (!m) return null;
        const month = this.MONTHS.indexOf(m[2]);
        if (month < 0) return null;
        return this.startOfDay(new Date(Number(m[3]), month, Number(m[1])));
      }

      // A stable fingerprint of each line, ignoring our own scheduling keys (which
      // we add/remove) so those changes never look like a "new" item.
      cartSignatures(items) {
        const keys = PickupDeliveryScheduler.SCHED_KEYS;
        return items.map((it) => {
          const props = Object.assign({}, it.properties);
          keys.forEach((k) => delete props[k]);
          const sig = Object.keys(props)
            .sort()
            .map((k) => `${k}=${props[k]}`)
            .join('&');
          return `${it.id}|${sig}`;
        });
      }

      countMap(arr) {
        return arr.reduce((m, s) => {
          m[s] = (m[s] || 0) + 1;
          return m;
        }, {});
      }

      hasNewLine(currentSigs) {
        const prev = this.countMap(this._knownSigs || []);
        const curr = this.countMap(currentSigs);
        // A genuinely new line means a signature appears more often than before
        // (covers both a brand-new product and another copy with new options).
        return Object.keys(curr).some((sig) => curr[sig] > (prev[sig] || 0));
      }

      subscribeCartUpdates() {
        if (typeof subscribe !== 'function') return;
        const evt = (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) || 'cart-update';
        this._unsub = subscribe(evt, () => this.onCartUpdated());
      }

      async onCartUpdated() {
        try {
          const cart = await this.fetchCart();
          const sigs = this.cartSignatures(cart.items || []);
          if (this.hasNewLine(sigs)) {
            // A new line item was added → clear the date & time. Re-selecting then
            // re-applies the same properties to every line, including the new one.
            this.resetSelection();
          }
          this._knownSigs = sigs;
        } catch (e) {
          console.error('[pds] cart update handling failed', e);
        }
      }

      resetSelection() {
        this.state.date = null;
        this.state.time = '';
        this.closePanel();
        this.renderCalendar();
        this.renderSlots();
        this.renderTriggerSummary();
        this.enforceCheckout();
        this.saveToCart(); // selection empty → strips scheduling props from all lines
      }

      /* ---------- state ---------- */
      restoreState() {
        // Synchronous starting point used for the first render, before the cart
        // has been fetched. Date/time start empty and the method defaults to the
        // first enabled option. initCart() then restores a still-valid saved
        // selection from the cart lines (see restoreFromCart), or strips a stale
        // one so the customer re-picks.
        return { method: this.defaultMethod(), date: null, time: '' };
      }

      defaultMethod() {
        if (this.config.pickupEnabled) return 'pickup';
        if (this.config.deliveryEnabled) return 'delivery';
        return 'pickup';
      }

      persistLocal() {
        try {
          localStorage.setItem(
            this.STORAGE_KEY,
            JSON.stringify({
              method: this.state.method,
              date: this.state.date ? this.toISO(this.state.date) : null,
              time: this.state.time,
            })
          );
        } catch (e) {
          /* ignore */
        }
      }

      /* ---------- events ---------- */
      bindEvents() {
        this.methodBtns.forEach((btn) => {
          btn.addEventListener('click', () => this.selectMethod(btn.dataset.method));
        });

        this.trigger.addEventListener('click', () => this.togglePanel());

        this.prevBtn.addEventListener('click', () => this.shiftMonth(-1));
        this.nextBtn.addEventListener('click', () => this.shiftMonth(1));

        // Close the panel on a genuine outside click / Escape.
        // Use composedPath() (captured at event dispatch) rather than contains(),
        // because selecting a date rebuilds the calendar and detaches the clicked
        // node — contains() would then wrongly treat it as an outside click.
        this.onDocClick = (e) => {
          if (this.panel.hidden) return;
          const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
          if (path.includes(this) || this.contains(e.target)) return;
          this.closePanel();
        };
        this.onKey = (e) => {
          if (e.key === 'Escape') this.closePanel();
        };
        document.addEventListener('click', this.onDocClick);
        document.addEventListener('keydown', this.onKey);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocClick);
        document.removeEventListener('keydown', this.onKey);
        if (this._unsub) this._unsub();
      }

      /* ---------- method toggle ---------- */
      selectMethod(method) {
        if (this.state.method === method) return;
        this.state.method = method;
        // Reset time slots that may differ; keep the date if still valid.
        this.renderMethod();
        if (this.state.date) this.renderSlots();
        this.persistLocal();
        this.saveToCart();
        this.renderTriggerSummary();
        this.enforceCheckout();
      }

      renderMethod() {
        const isPickup = this.state.method === 'pickup';
        this.methodBtns.forEach((btn) => {
          const active = btn.dataset.method === this.state.method;
          btn.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        this.locationEls.forEach((el) => {
          el.classList.toggle('is-active', el.dataset.forMethod === this.state.method);
        });
        // Delivery has no scheduling — hide the date/time picker entirely and
        // make sure its panel is never left open when switching to delivery.
        if (this.dateField) this.dateField.hidden = !isPickup;
        if (!isPickup) this.closePanel();
      }

      /* ---------- popover ---------- */
      togglePanel() {
        if (this.panel.hidden) this.openPanel();
        else this.closePanel();
      }

      openPanel() {
        this.panel.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        if (this.state.date) {
          this.viewDate = new Date(this.state.date);
          this.viewDate.setDate(1);
          this.renderCalendar();
          this.renderSlots();
        }
      }

      closePanel() {
        this.panel.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
      }

      shiftMonth(delta) {
        this.viewDate.setMonth(this.viewDate.getMonth() + delta);
        this.renderCalendar();
      }

      /* ---------- calendar ---------- */
      renderCalendar() {
        const year = this.viewDate.getFullYear();
        const month = this.viewDate.getMonth();
        this.calTitle.textContent = `${this.MONTHS[month]} ${year}`;

        // Disable nav buttons outside the allowed window
        const firstOfView = new Date(year, month, 1);
        const lastOfView = new Date(year, month + 1, 0);
        this.prevBtn.disabled = firstOfView <= new Date(this.minDate.getFullYear(), this.minDate.getMonth(), 1);
        this.nextBtn.disabled = lastOfView >= new Date(this.maxDate.getFullYear(), this.maxDate.getMonth() + 1, 0);

        let html = this.DOW.map((d) => `<span class="pds__cal-dow">${d}</span>`).join('');

        // Leading blanks (Monday-first): JS getDay 0=Sun..6=Sat -> col (getDay+6)%7
        const firstCol = (firstOfView.getDay() + 6) % 7;
        for (let i = 0; i < firstCol; i++) {
          html += '<span class="pds__cal-day is-empty"></span>';
        }

        const daysInMonth = lastOfView.getDate();
        const todayISO = this.toISO(this.startOfDay(new Date()));
        const selISO = this.state.date ? this.toISO(this.state.date) : null;

        for (let day = 1; day <= daysInMonth; day++) {
          const date = this.startOfDay(new Date(year, month, day));
          const iso = this.toISO(date);
          const disabled = date < this.minDate || date > this.maxDate || this.isClosed(date);
          const classes = ['pds__cal-day'];
          if (iso === todayISO) classes.push('is-today');
          if (iso === selISO) classes.push('is-selected');
          html += `<button type="button" class="${classes.join(' ')}" data-iso="${iso}"${disabled ? ' disabled' : ''}>${day}</button>`;
        }

        this.calGrid.innerHTML = html;
        this.calGrid.querySelectorAll('.pds__cal-day[data-iso]:not([disabled])').forEach((btn) => {
          btn.addEventListener('click', () => this.selectDate(btn.dataset.iso));
        });
      }

      selectDate(iso) {
        this.state.date = this.startOfDay(this.fromISO(iso));
        this.state.time = ''; // reset time when date changes
        this.renderCalendar();
        this.renderSlots();
        this.persistLocal();
        this.saveToCart();
        this.renderTriggerSummary();
        this.enforceCheckout();
      }

      /* ---------- time slots ---------- */
      renderSlots() {
        if (!this.state.date) {
          this.slotsWrap.hidden = true;
          return;
        }
        const slots = this.slotsForDate(this.state.date);
        this.slotsWrap.hidden = false;

        if (!slots.length) {
          this.slotsList.innerHTML = '<p class="pds__slots-empty">Ingen tider tilgjengelig denne dagen.</p>';
          return;
        }

        this.slotsList.innerHTML = slots
          .map((s) => {
            const active = s === this.state.time ? ' is-selected' : '';
            return `<button type="button" class="pds__slot${active}" data-slot="${s}">${s}</button>`;
          })
          .join('');

        this.slotsList.querySelectorAll('.pds__slot').forEach((btn) => {
          btn.addEventListener('click', () => this.selectSlot(btn.dataset.slot));
        });
      }

      selectSlot(slot) {
        this.state.time = slot;
        this.renderSlots();
        this.persistLocal();
        this.saveToCart();
        this.renderTriggerSummary();
        this.enforceCheckout();
        this.closePanel();
      }

      slotsForDate(date) {
        const day = this.config.schedule[String(date.getDay())];
        if (!day || day.closed) return [];
        const open = this.toMinutes(day.open);
        const close = this.toMinutes(day.close);
        const step = this.config.slotInterval || 60;
        if (open == null || close == null || close <= open) return [];

        const out = [];
        for (let start = open; start < close; start += step) {
          const end = Math.min(start + step, close);
          out.push(`${this.fromMinutes(start)} - ${this.fromMinutes(end)}`);
        }
        return out;
      }

      /* ---------- summary + cart ---------- */
      renderTriggerSummary() {
        const placeholder = this.triggerText.dataset.placeholder || this.triggerText.textContent;
        if (!this.triggerText.dataset.placeholder) {
          this.triggerText.dataset.placeholder = this.triggerText.textContent;
        }
        if (this.state.date && this.state.time) {
          this.triggerText.textContent = `${this.formatDateShort(this.state.date)} · ${this.state.time}`;
          this.trigger.classList.add('is-filled');
        } else {
          this.triggerText.textContent = placeholder;
          this.trigger.classList.remove('is-filled');
        }
      }

      // Keys this widget manages on each line item. Listed so we can strip stale
      // ones before re-writing (e.g. when switching pickup → delivery).
      static get SCHED_KEYS() {
        return ['Fulfillment Method', 'Date', 'Time', 'Pickup Location'];
      }

      // The value saved under "Fulfillment Method" for each method. Single source
      // of truth so scheduleProps() (write) and methodFromValue() (read-back on
      // load) can never drift apart.
      static get METHOD_LABELS() {
        return { pickup: 'Henting i butikk', delivery: 'Levering' };
      }

      scheduleProps() {
        if (!this.isComplete()) return {};
        const labels = PickupDeliveryScheduler.METHOD_LABELS;
        // Delivery is handled by a third party, so it carries no date/time/
        // location — only the chosen method is saved on the line item.
        if (this.state.method !== 'pickup') {
          return { 'Fulfillment Method': labels.delivery };
        }
        return {
          'Fulfillment Method': labels.pickup,
          Date: this.formatDateLong(this.state.date),
          Time: this.state.time,
          'Pickup Location': this.pickupLocationText(),
        };
      }

      // Persist the selection as LINE ITEM PROPERTIES on every cart item, so it
      // shows on the cart, the checkout page, and the order. Existing properties
      // (e.g. the sprinkle choice) are preserved by merging.
      saveToCart() {
        this._needsSave = true;
        // Reflect the in-progress state immediately: keep checkout disabled and
        // show the loader until the write actually completes.
        this.enforceCheckout();
        if (this._saving) return;
        this._runSave();
      }

      async _runSave() {
        this._saving = true;
        try {
          while (this._needsSave) {
            this._needsSave = false;
            await this._writeLineProps();
          }
        } catch (e) {
          console.error('[pds] saving line item properties failed', e);
        } finally {
          this._saving = false;
          // Save finished — re-enable checkout (if complete) and hide the loader.
          this.enforceCheckout();
        }
      }

      pendingSave() {
        return !!(this._saving || this._needsSave);
      }

      async _writeLineProps() {
        const sched = this.scheduleProps();
        const keys = PickupDeliveryScheduler.SCHED_KEYS;
        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        const changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change';

        const cart = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
          r.json()
        );
        if (!cart.items || !cart.items.length) return;

        let changed = false;

        // Identify each line by its 1-based index. The index is stable even though
        // a line's key changes when its properties change (unlike the key itself).
        for (let i = 0; i < cart.items.length; i++) {
          const item = cart.items[i];
          const properties = Object.assign({}, item.properties);
          keys.forEach((k) => delete properties[k]);
          Object.assign(properties, sched);

          // Skip lines that already have exactly these properties — avoids
          // needless requests (e.g. the on-load strip when nothing is stale).
          if (this.propsEqual(item.properties, properties)) continue;
          changed = true;

          const res = await fetch(changeUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line: i + 1, quantity: item.quantity, properties }),
          });
          if (!res.ok) {
            const text = await res.text();
            console.error(`[pds] change.js failed (${res.status}) for line ${i + 1}:`, text);
          }
        }

        if (!changed) return;

        // Verify what actually persisted, so we can confirm it in the console.
        const after = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
          r.json()
        );
        console.log(
          '[pds] line properties now on cart →',
          after.items.map((it) => it.properties)
        );
      }

      propsEqual(a, b) {
        a = a || {};
        b = b || {};
        const ak = Object.keys(a);
        const bk = Object.keys(b);
        if (ak.length !== bk.length) return false;
        return ak.every((k) => a[k] === b[k]);
      }

      pickupLocationText() {
        const el = this.querySelector('[data-for-method="pickup"]');
        if (!el) return '';
        return el.textContent.replace(/\s+/g, ' ').trim();
      }

      /* ---------- checkout enforcement ---------- */
      // Checkout is blocked until a method + date + time are chosen AND the
      // selection has finished saving to the line items. This closes the race
      // where a fast click could check out before the properties were written.
      enforceCheckout() {
        const complete = this.isComplete();
        const pending = this.pendingSave();
        const valid = complete && !pending;

        const checkoutBtns = document.querySelectorAll('#checkout, #CartDrawer-Checkout');
        checkoutBtns.forEach((btn) => {
          // Never re-enable an empty cart's button.
          if (btn.dataset.pdsManaged === undefined && btn.disabled && !valid) {
            btn.dataset.pdsManaged = 'true';
          }
          if (valid) {
            if (btn.dataset.pdsManaged === 'true') {
              btn.disabled = false;
              delete btn.dataset.pdsManaged;
            }
          } else {
            btn.dataset.pdsManaged = 'true';
            btn.disabled = true;
          }
        });

        document.querySelectorAll('.cart__dynamic-checkout-buttons').forEach((wrap) => {
          wrap.classList.toggle('pds-checkout-blocked', !valid);
        });

        // Messaging: loader while saving a complete selection, otherwise the
        // "what's missing" hint, or nothing once valid and saved.
        if (complete && pending) {
          this.hideError();
          this.showLoader();
        } else if (!complete) {
          this.hideLoader();
          this.showError(this.errorMessage());
        } else {
          this.hideLoader();
          this.hideError();
        }
      }

      showLoader() {
        if (this.loaderEl) this.loaderEl.hidden = false;
      }

      hideLoader() {
        if (this.loaderEl) this.loaderEl.hidden = true;
      }

      errorMessage() {
        if (!this.state.method) return 'Velg henting eller levering.';
        if (!this.state.date) return 'Velg en dato.';
        if (!this.state.time) return 'Velg et tidspunkt.';
        return '';
      }

      showError(msg) {
        if (!msg) return this.hideError();
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
      }

      hideError() {
        this.errorEl.hidden = true;
      }

      /* ---------- helpers ---------- */
      isClosed(date) {
        const day = this.config.schedule[String(date.getDay())];
        return !day || day.closed || !this.slotsForDate(date).length;
      }

      startOfDay(d) {
        const x = new Date(d);
        x.setHours(0, 0, 0, 0);
        return x;
      }

      toISO(d) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      }

      fromISO(iso) {
        const [y, m, d] = iso.split('-').map(Number);
        return new Date(y, m - 1, d);
      }

      toMinutes(hhmm) {
        if (!hhmm || hhmm.indexOf(':') === -1) return null;
        const [h, m] = hhmm.split(':').map(Number);
        if (Number.isNaN(h) || Number.isNaN(m)) return null;
        return h * 60 + m;
      }

      fromMinutes(total) {
        const h = Math.floor(total / 60);
        const m = total % 60;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      }

      formatDateLong(d) {
        return `${this.DAY_NAMES[d.getDay()]}, ${d.getDate()} ${this.MONTHS[d.getMonth()]} ${d.getFullYear()}`;
      }

      formatDateShort(d) {
        return `${this.DOW[(d.getDay() + 6) % 7]}, ${d.getDate()} ${this.MONTHS[d.getMonth()].slice(0, 3)} ${d.getFullYear()}`;
      }
    }
  );
}
